import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InsightCommonProps, InsightContextState } from "../types";
import { StreamingState, StreamStatus } from "@/modules/streaming/types";
import { InsightStatus } from "../constants";

export type InsightStateProps = InsightCommonProps & StreamingState & InsightContextState;

export const initialState: InsightStateProps = {
  opportunities: [],
  selectedOpportunityId: null,
  totalRuns: "",
  startDate: "",
  endDate: "",
  explanationEndpoint: "/opportunities/explanation",
  showMultiStatementError: false,
  extraContent: undefined,
  showScheduledEventsActivity: false,
  queryId: undefined,
  showActionsDropdown: false,
  integrationIds: undefined,
  description: undefined,
  sqlLineage: undefined,
  queryText: undefined,
  streamStatus: {},
  streamContent: {},
  isLoading: {},
  selectedTab: [InsightStatus.ACTIVE],
  sort: {},
};

const insightSlice = createSlice({
  name: "insightState",
  initialState,
  reducers: {
    setOpportunities: (state, action: PayloadAction<InsightStateProps["opportunities"]>) => {
      state.opportunities = action.payload;
    },
    setSelectedOpportunityId: (
      state,
      action: PayloadAction<InsightStateProps["selectedOpportunityId"]>
    ) => {
      state.selectedOpportunityId = action.payload;
    },
    setInsightState: (state, action: PayloadAction<Partial<InsightStateProps>>) => {
      // Don't override streamStatus and streamContent if not provided
      const { streamStatus, streamContent, ...otherProps } = action.payload;
      return {
        ...state,
        ...otherProps,
        streamStatus: streamStatus ?? state.streamStatus,
        streamContent: streamContent ?? state.streamContent,
      };
    },
    setTotalRuns: (state, action: PayloadAction<InsightStateProps["totalRuns"]>) => {
      state.totalRuns = action.payload;
    },
    setStartDate: (state, action: PayloadAction<InsightStateProps["startDate"]>) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action: PayloadAction<InsightStateProps["endDate"]>) => {
      state.endDate = action.payload;
    },
    setExplanationEndpoint: (
      state,
      action: PayloadAction<InsightStateProps["explanationEndpoint"]>
    ) => {
      state.explanationEndpoint = action.payload;
    },
    setShowMultiStatementError: (
      state,
      action: PayloadAction<InsightStateProps["showMultiStatementError"]>
    ) => {
      state.showMultiStatementError = action.payload;
    },
    setExtraContent: (state, action: PayloadAction<InsightStateProps["extraContent"]>) => {
      state.extraContent = action.payload;
    },
    setShowScheduledEventsActivity: (
      state,
      action: PayloadAction<InsightStateProps["showScheduledEventsActivity"]>
    ) => {
      state.showScheduledEventsActivity = action.payload;
    },
    setQueryId: (state, action: PayloadAction<InsightStateProps["queryId"]>) => {
      state.queryId = action.payload;
    },
    setShowActionsDropdown: (
      state,
      action: PayloadAction<InsightStateProps["showActionsDropdown"]>
    ) => {
      state.showActionsDropdown = action.payload;
    },
    setIntegrationIds: (state, action: PayloadAction<InsightStateProps["integrationIds"]>) => {
      state.integrationIds = action.payload;
    },
    setStreamStatus: (
      state,
      action: PayloadAction<{ opportunityId: string; status: StreamStatus | undefined }>
    ) => {
      const { opportunityId, status } = action.payload;
      if (status === undefined) {
        delete state.streamStatus[opportunityId];
      } else {
        state.streamStatus[opportunityId] = status;
      }
    },
    setStreamContent: (
      state,
      action: PayloadAction<{ opportunityId: string; content: string }>
    ) => {
      const { opportunityId, content } = action.payload;
      state.streamContent[opportunityId] = content;
    },
    appendStreamContent: (
      state,
      action: PayloadAction<{ opportunityId: string; content: string }>
    ) => {
      const { opportunityId, content } = action.payload;
      state.streamContent[opportunityId] = (state.streamContent[opportunityId] || "") + content;
    },
    clearStreamData: (state, action: PayloadAction<string>) => {
      const opportunityId = action.payload;
      delete state.streamStatus[opportunityId];
      delete state.streamContent[opportunityId];
    },
    setStreamData: (
      state,
      action: PayloadAction<{
        opportunityId: string;
        status?: StreamStatus;
        content?: string;
      }>
    ) => {
      const { opportunityId, status, content } = action.payload;
      if (status !== undefined) {
        state.streamStatus[opportunityId] = status;
      }
      if (content !== undefined) {
        state.streamContent[opportunityId] = content;
      }
    },
    setSelectedTab: (state, action: PayloadAction<InsightStateProps["selectedTab"]>) => {
      state.selectedTab = action.payload;
    },
    setSort: (state, action: PayloadAction<InsightStateProps["sort"]>) => {
      state.sort = action.payload;
    },
  },
});

export const {
  setOpportunities,
  setSelectedOpportunityId,
  setInsightState,
  setTotalRuns,
  setStartDate,
  setEndDate,
  setExplanationEndpoint,
  setShowMultiStatementError,
  setExtraContent,
  setShowScheduledEventsActivity,
  setQueryId,
  setShowActionsDropdown,
  setIntegrationIds,
  setStreamStatus,
  setStreamContent,
  appendStreamContent,
  clearStreamData,
  setStreamData,
  setSelectedTab,
  setSort,
} = insightSlice.actions;

export default insightSlice;
